import styled from '@emotion/styled';
import { FC, JSX, ReactNode } from 'react';
import ReactContentLoader from 'react-content-loader';

import Container from 'app/components/layout/Container';
import Box from 'app/components/v2/components/Box';
import Flex from 'app/components/v2/components/Flex';
import LoadingSpinner from 'app/components/v2/components/LoadingSpinner';
import {
  showPageAsideClassName,
  ShowPageContainer,
  showPageContainerClassName,
} from 'app/components/v2/layout/ShowPage/styles';
import { getSemanticColorVariable } from 'app/components/v2/theme/colors';

// Local Typings
export type SkeletonType =
  'billEdit' |
  'check' |
  'grid' |
  'list' |
  'payeeSelectCarrier' |
  'payorBill' |
  'profile' |
  'show' |
  'table' |
  'userEdit';

interface Props {
  skeletonType?: SkeletonType;
}

// Local Variables
const ContentLoader = ({
  children,
  height,
}: {
  children: ReactNode;
  height: number;
}) => {
  return (
    <ReactContentLoader
      backgroundColor={getSemanticColorVariable('skeleton_background')}
      foregroundColor={getSemanticColorVariable('skeleton_foreground')}
      height={height}
      width="100%"
    >
      {children}
    </ReactContentLoader>
  );
};

export const skeletonTransitionSpeedInMs = 600;
const v2TableToolbarHeight = 72;
const tableRowHeight = 50;
const tableOffsetY = 18;
export const AnimatedWrapper = styled.div<{ opacity: number }>(({ opacity }) => ({
  height: '100%',
  opacity,
  transition: `${skeletonTransitionSpeedInMs}ms`,
}));

const rowArray = new Array(10).fill(true);

const BillEditLoader = () => (
  <ContentLoader height={1000}>
    <rect
      height="140"
      rx="5"
      ry="5"
      width="30%"
      x="0"
      y="0"
    />

    <rect
      height="200"
      rx="5"
      ry="5"
      width="30%"
      x="0"
      y="170"
    />

    <rect
      height="170"
      rx="5"
      ry="5"
      width="65%"
      x="35%"
      y="0"
    />

    <rect
      height="240"
      rx="5"
      ry="5"
      width="65%"
      x="35%"
      y="200"
    />

    <rect
      height="300"
      rx="5"
      ry="5"
      width="65%"
      x="35%"
      y="470"
    />
  </ContentLoader>
);

const CheckImageLoader = () => (
  <ContentLoader height={320}>
    <rect
      height="130"
      rx="3"
      ry="3"
      width="90%"
      x="20"
      y="10"
    />
    <rect
      height="150"
      rx="3"
      ry="3"
      width="90%"
      x="20"
      y="160"
    />
  </ContentLoader>
);

const GridItemLoader = () => (
  <ContentLoader height={305}>
    <rect
      height="100%"
      rx="3"
      ry="3"
      width="100%"
      x="0"
      y="0"
    />
  </ContentLoader>
);

const GridLoader = () => (
  <Box>
    <ContentLoader height={v2TableToolbarHeight}>
      <rect
        height="48"
        rx="5"
        ry="5"
        width="100%"
        x="0"
        y={tableOffsetY}
      />
    </ContentLoader>

    <Box
      display="grid"
      fullWidth
      gap={6}
      gridTemplateColumns="repeat(auto-fill, minmax(304px, 1fr))"
    >
      <GridItemLoader />
      <GridItemLoader />
      <GridItemLoader />
      <GridItemLoader />
      <GridItemLoader />
      <GridItemLoader />
    </Box>
  </Box>
);

const ListLoader = () => (
  <ContentLoader height={400}>
    {rowArray.map((_row, index) => (
      <rect
        height={40}
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        rx="4"
        ry="4"
        width="100%"
        x="0"
        y={(index + 1) * (40 + 1)}
      />
    ))}
  </ContentLoader>
);

const PayeeSelectCarrierLoader = () => (
  <Box
    display="flex"
    gap={6}
  >
    <Box
      display="flex"
      fullWidth
      maxWidth="70%"
    >
      <ContentLoader height={302}>
        <rect
          height="100%"
          rx="3"
          ry="3"
          width="100%"
          x="0"
          y="0"
        />
      </ContentLoader>
    </Box>

    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
    >
      <ContentLoader height={546}>
        <rect
          height="100%"
          rx="3"
          ry="3"
          width="100%"
          x="0"
          y="0"
        />
      </ContentLoader>
    </Box>
  </Box>
);

const PayorBillLoader = () => (
  <ContentLoader height={400}>
    <rect
      height="200"
      rx="5"
      ry="5"
      width="60%"
      x="0"
      y="20"
    />

    <rect
      height="95"
      rx="5"
      ry="5"
      width="35%"
      x="65%"
      y="20"
    />

    <rect
      height="95"
      rx="5"
      ry="5"
      width="35%"
      x="65%"
      y="125"
    />
  </ContentLoader>
);

const ProfileLoader = () => (
  <ContentLoader height={400}>
    <rect
      height="100"
      rx="5"
      ry="5"
      width="60%"
      x="0"
      y="20"
    />

    <rect
      height="130"
      rx="5"
      ry="5"
      width="35%"
      x="65%"
      y="20"
    />

    <rect
      height="50"
      rx="5"
      ry="5"
      width="60%"
      x="0"
      y="170"
    />

    <rect
      height="100"
      rx="5"
      ry="5"
      width="35%"
      x="65%"
      y="170"
    />
  </ContentLoader>
);

const ShowItemLoader = () => (
  <div style={{ width: '100%' }}>
    <ContentLoader height={400}>
      <rect
        height="100%"
        rx="3"
        ry="3"
        width="100%"
        x="0"
        y="0"
      />
    </ContentLoader>
  </div>
);

const ShowLoader = () => (
  <ShowPageContainer>
    <Box
      className={showPageContainerClassName}
      display="flex"
      flexDirection="column"
      fullWidth
    >
      <ShowItemLoader />
    </Box>

    <Box
      className={showPageAsideClassName}
      display="flex"
      flexDirection="column"
      flexGrow={1}
    >
      <ShowItemLoader />
    </Box>
  </ShowPageContainer>
);

const TableLoader = () => (
  <ContentLoader height={600}>
    <rect
      height={v2TableToolbarHeight}
      rx="5"
      ry="5"
      width="100%"
      x="0"
      y={tableOffsetY}
    />

    {rowArray.map((_row, index) => (
      <rect
        height={tableRowHeight}
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        rx="4"
        ry="4"
        width="100%"
        x="0"
        y={(index + 1) * (tableRowHeight + 1) + tableOffsetY + 3}
      />
    ))}
  </ContentLoader>
);

const UserEditLoader = () => (
  <ContentLoader height={400}>
    <rect
      height="80"
      rx="5"
      ry="5"
      width="48%"
      x="0"
      y={tableOffsetY}
    />
    <rect
      height="100"
      rx="5"
      ry="5"
      width="48%"
      x="52%"
      y={tableOffsetY}
    />
    <rect
      height="100"
      rx="5"
      ry="5"
      width="48%"
      x="0"
      y={110}
    />
  </ContentLoader>
);

function getTable(skeletonType?: SkeletonType) {
  const skeletons: Record<SkeletonType, JSX.Element> = {
    billEdit: <BillEditLoader />,
    check: <CheckImageLoader />,
    grid: <GridLoader />,
    list: <ListLoader />,
    payeeSelectCarrier: <PayeeSelectCarrierLoader />,
    payorBill: <PayorBillLoader />,
    profile: <ProfileLoader />,
    show: <ShowLoader />,
    table: <TableLoader />,
    userEdit: <UserEditLoader />,
  };

  return skeletonType
    ? skeletons[skeletonType]
    : (
      <Flex justifyContent="center">
        <LoadingSpinner />
      </Flex>
    );
}

const SkeletonLoader: FC<Props> = ({ skeletonType }) => (
  <Container>
    {getTable(skeletonType)}
  </Container>
);

export default SkeletonLoader;
